import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { ProductDetail, SafetyHazard, SafetyEquipment } from '../../../../types/ProductTypes';
import { SectionCard, SectionTitle, InfoGrid, InfoItem } from '../../Section.styles';
import { useDownloadFile } from '../../../../api/useDownloadFile';
import {
    SafetyAccordionWrapper,
    InfoBoxWrapper,
    InfoBox,
    InfoBoxContentNormal,
    InfoBoxContentHigh,
    InfoBoxContentExtreme,
    LevelNumberBox,
    LevelNumber,
    InfoTextContent,
    DescTextContent,
    DetailsColumn,
    HazardList,
    HazardItem,
    HazardCode,
    HazardDescription,
    ImageWrapper,
    ChangedManuallyMessage,
    NAMessage,
    CommentsSection,
    CommentsLabel,
    CommentsText,
    EmergencyScenariosTable,
    DraegerTubesList,
    DraegerTubeLink,
    ExternalLink,
    EditModeOverwriteSection,
    OverwriteLabel,
    OverwriteInput,
    GeneratedValueLabel,
    HazardSelect,
    HazardRemoveButton,
    EditSelect,
    EditInput,
    EditTextarea,
    EditFieldWrapper,
    InputSuffix,
    DraegerTubeItem,
    DraegerTubeRemoveButton,
    AddDraegerTubeWrapper,
    CommentsEditSection,
    CommentsEditLabel,
    StackedInfoSection,
    MsdsLinkWrapper,
} from './SafetyInformationSection.styles';
import {
    getExposureLevels,
    isManuallyChanged as checkManuallyChanged,
    hasNALevel as checkNALevel,
    calculateHazardExposureLevels,
} from '../../../../utils/safetyEquipmentUtils';
import { useEditMode } from '../../../../context/EditModeContext';
import { useAuthStore } from '../../../../context/AuthContext';
import { useProductEdit } from '../../../../context/ProductEditContext';
import useGetSafetyHazards from '../../../../api/useGetSafetyHazards';
import { SafetyHazardItem } from '../../../../types/SafetyHazardTypes';
import useGetDraegerTubes from '../../../../api/useGetDraegerTubes';

interface SafetyInformationSectionProps {
    product: ProductDetail;
    isProductCategory?: boolean;
}

const SafetyInformationSection: React.FC<SafetyInformationSectionProps> = ({ product, isProductCategory = true }) => {
    const { downloadFile } = useDownloadFile();
    const { isInEditMode, isInChemGuardEditMode } = useEditMode();
    const { canEdit, canEditChemGuard } = useAuthStore();
    const productEditContext = useProductEdit();
    const mergePendingChanges = productEditContext?.mergePendingChanges;

    // Fetch all available safety hazards for the dropdown
    const { safetyHazards: allHazards, getSafetyHazards } = useGetSafetyHazards();

    // Fetch all available Dräger tubes for the dropdown
    const { draegerTubes: allDraegerTubes, getDraegerTubes } = useGetDraegerTubes();

    const [openDescriptions, setOpenDescriptions] = useState<{ [key: string]: boolean }>({
        normal: false,
        high: false,
        extreme: false,
    });

    // Local state for exposure level overrides (only used in edit mode)
    const [localOverrides, setLocalOverrides] = useState<{
        normalExposureLevel: number | null;
        highExposureLevel: number | null;
        extremeExposureLevel: number | null;
    }>({
        normalExposureLevel: null,
        highExposureLevel: null,
        extremeExposureLevel: null,
    });

    // Local state for hazards (only used in edit mode)
    const [localHazards, setLocalHazards] = useState<SafetyHazard[]>([]);
    const [selectedHazardId, setSelectedHazardId] = useState<string>('');

    // Local state for category fields (Static Accumulator, Threshold Limit, Dräger Tubes)
    const [localStaticAccumulator, setLocalStaticAccumulator] = useState<number>(0);
    const [localThresholdLimit, setLocalThresholdLimit] = useState<number | null>(null);
    const [localDraegerTubes, setLocalDraegerTubes] = useState<any[]>([]);
    const [selectedDraegerTubeId, setSelectedDraegerTubeId] = useState<string>('');

    // Local state for Comments (MSG) - ProductComments category 11
    const [localCommentsMSG, setLocalCommentsMSG] = useState<string>('');

    // Determine if we're in edit mode for this section
    const showEditUI = canEdit && (isInEditMode || isInChemGuardEditMode) && isProductCategory;

    // Fetch hazards when entering edit mode
    useEffect(() => {
        if (showEditUI && allHazards.length === 0) {
            getSafetyHazards();
        }
    }, [showEditUI, allHazards.length, getSafetyHazards]);

    // Fetch Dräger tubes when entering edit mode
    useEffect(() => {
        if (showEditUI && allDraegerTubes.length === 0) {
            getDraegerTubes();
        }
    }, [showEditUI, allDraegerTubes.length, getDraegerTubes]);

    const toggleDescription = (type: string) => {
        setOpenDescriptions((prev) => ({
            ...prev,
            [type]: !prev[type],
        }));
    };

    const safetyEquipment = product.category?.safetyEquipment;
    const safetyHazards = safetyEquipment?.safetyHazards || [];

    // Initialize local state from product when entering edit mode
    useEffect(() => {
        if (showEditUI && safetyEquipment) {
            setLocalOverrides({
                normalExposureLevel: safetyEquipment.normalExposureLevel,
                highExposureLevel: safetyEquipment.highExposureLevel,
                extremeExposureLevel: safetyEquipment.extremeExposureLevel,
            });
            setLocalHazards([...safetyHazards]);
        }
        // Initialize category fields
        if (showEditUI && product.category) {
            setLocalStaticAccumulator(product.category.isStaticAccumulator ?? 0);
            setLocalThresholdLimit(product.category.thresholdLimitValueTimeWeightedAverage ?? null);
            setLocalDraegerTubes([...(product.category.draegerTubes || [])]);
        }
        // Initialize Comments (MSG)
        if (showEditUI) {
            const msgComment = product.comments?.find((c) => c.category === 11);
            setLocalCommentsMSG(msgComment?.text || '');
        }
    }, [showEditUI, safetyEquipment, product.category, product.comments]);

    // Calculate exposure levels using utility functions
    // In edit mode, recalculate based on localHazards; otherwise use product data
    const exposureLevels = useMemo(() => {
        if (!safetyEquipment) {
            return {
                normalExposureLevelGenerated: null,
                highExposureLevelGenerated: null,
                extremeExposureLevelGenerated: null,
                normalExposureLevelActive: null,
                highExposureLevelActive: null,
                extremeExposureLevelActive: null,
            };
        }

        // In edit mode, always recalculate generated levels from local hazards
        if (showEditUI) {
            const generatedLevels = calculateHazardExposureLevels({
                ...safetyEquipment,
                safetyHazards: localHazards,
            });

            // Active levels use override if set, otherwise use generated
            return {
                ...generatedLevels,
                normalExposureLevelActive: localOverrides.normalExposureLevel ?? generatedLevels.normalExposureLevelGenerated,
                highExposureLevelActive: localOverrides.highExposureLevel ?? generatedLevels.highExposureLevelGenerated,
                extremeExposureLevelActive: localOverrides.extremeExposureLevel ?? generatedLevels.extremeExposureLevelGenerated,
            };
        }

        return getExposureLevels(safetyEquipment);
    }, [safetyEquipment, showEditUI, localHazards, localOverrides]);

    // Extract active levels for display
    const normalLevel = exposureLevels.normalExposureLevelActive;
    const highLevel = exposureLevels.highExposureLevelActive;
    const extremeLevel = exposureLevels.extremeExposureLevelActive;

    // Get protection equipment lists based on exposure levels
    // TODO: These should come from API: safetyEquipment.protectionEquipmentList[normalExposureLevelActive]
    const normalEquipment = (safetyEquipment as any)?.protectionEquipmentList?.[normalLevel || 0] || [];
    const highEquipment = (safetyEquipment as any)?.protectionEquipmentList?.[highLevel || 0] || [];
    const extremeEquipment = (safetyEquipment as any)?.protectionEquipmentList?.[extremeLevel || 0] || [];

    // Check if manually changed or has N/A levels
    const manuallyChanged = safetyEquipment ? checkManuallyChanged(safetyEquipment) : false;
    const naLevel = safetyEquipment
        ? checkNALevel(safetyEquipment, {
              normalExposureLevelGenerated: exposureLevels.normalExposureLevelGenerated,
              highExposureLevelGenerated: exposureLevels.highExposureLevelGenerated,
              extremeExposureLevelGenerated: exposureLevels.extremeExposureLevelGenerated,
          })
        : false;

    // Handler for overwriting exposure levels
    const handleExposureLevelChange = useCallback(
        (level: 'normal' | 'high' | 'extreme', value: string) => {
            const numValue = value === '' ? null : Math.min(4, Math.max(0, parseInt(value, 10)));

            const newOverrides = { ...localOverrides };
            if (level === 'normal') newOverrides.normalExposureLevel = numValue;
            if (level === 'high') newOverrides.highExposureLevel = numValue;
            if (level === 'extreme') newOverrides.extremeExposureLevel = numValue;

            setLocalOverrides(newOverrides);

            // Push to pending changes
            if (mergePendingChanges && safetyEquipment) {
                const updatedSafetyEquipment: SafetyEquipment = {
                    ...safetyEquipment,
                    normalExposureLevel: newOverrides.normalExposureLevel,
                    highExposureLevel: newOverrides.highExposureLevel,
                    extremeExposureLevel: newOverrides.extremeExposureLevel,
                    safetyHazards: showEditUI ? localHazards : safetyHazards,
                };
                console.log('[SafetyInfo] Merging safetyEquipment:', updatedSafetyEquipment);
                console.log('[SafetyInfo] Merging safetyHazards count:', updatedSafetyEquipment.safetyHazards?.length);
                mergePendingChanges({
                    category: {
                        ...product.category,
                        safetyEquipment: updatedSafetyEquipment,
                    } as any,
                });
            }
        },
        [localOverrides, localHazards, mergePendingChanges, safetyEquipment, product.category, showEditUI, safetyHazards]
    );

    // Handler for adding a hazard to the product
    const handleAddHazard = useCallback(
        (hazardId: string) => {
            if (!hazardId || !safetyEquipment) return;

            const hazardToAdd = allHazards.find((h: SafetyHazardItem) => h.id.toString() === hazardId);
            if (!hazardToAdd) return;

            // Check if hazard already exists
            if (localHazards.some((h) => h.id === hazardToAdd.id)) {
                setSelectedHazardId('');
                return;
            }

            // Convert SafetyHazardItem to SafetyHazard
            const newHazard: SafetyHazard = {
                id: hazardToAdd.id,
                hazardCode: hazardToAdd.hazardCode || hazardToAdd.HazardCode || '',
                hazardDescription: hazardToAdd.hazardDescription || hazardToAdd.HazardDescription || '',
                normalExposureLevel: hazardToAdd.normalExposureLevel ?? hazardToAdd.NormalExposureLevel ?? 0,
                highExposureLevel: hazardToAdd.highExposureLevel ?? hazardToAdd.HighExposureLevel ?? 0,
                extremeExposureLevel: hazardToAdd.extremeExposureLevel ?? hazardToAdd.ExtremeExposureLevel ?? 0,
                warningImage: hazardToAdd.warningImage || hazardToAdd.WarningImage || '',
                category: hazardToAdd.category || hazardToAdd.Category || '',
                warningLevel: hazardToAdd.warningLevel ?? hazardToAdd.WarningLevel ?? 0,
                physicalPropertiesScore: hazardToAdd.physicalPropertiesScore ?? hazardToAdd.PhysicalPropertiesScore ?? 0,
                healthPropertiesScore: hazardToAdd.healthPropertiesScore ?? hazardToAdd.HealthPropertiesScore ?? 0,
                environmentPropertiesScore: hazardToAdd.environmentPropertiesScore ?? hazardToAdd.EnvironmentPropertiesScore ?? 0,
                requiresExposureLog: hazardToAdd.requiresExposureLog ?? hazardToAdd.RequiresExposureLog ?? false,
                isActive: hazardToAdd.isActive ?? hazardToAdd.IsActive ?? true,
                lastUpdatedUtc: hazardToAdd.lastUpdatedUtc || hazardToAdd.LastUpdatedUtc || new Date().toISOString(),
            };

            const updatedHazards = [...localHazards, newHazard];
            setLocalHazards(updatedHazards);
            setSelectedHazardId('');

            // Push to pending changes
            if (mergePendingChanges) {
                const updatedSafetyEquipment: SafetyEquipment = {
                    ...safetyEquipment,
                    normalExposureLevel: localOverrides.normalExposureLevel,
                    highExposureLevel: localOverrides.highExposureLevel,
                    extremeExposureLevel: localOverrides.extremeExposureLevel,
                    safetyHazards: updatedHazards,
                };
                mergePendingChanges({
                    category: {
                        ...product.category,
                        safetyEquipment: updatedSafetyEquipment,
                    } as any,
                });
            }
        },
        [allHazards, localHazards, localOverrides, mergePendingChanges, safetyEquipment, product.category]
    );

    // Handler for removing a hazard from the product
    const handleRemoveHazard = useCallback(
        (hazardId: number) => {
            if (!safetyEquipment) return;

            const updatedHazards = localHazards.filter((h) => h.id !== hazardId);
            setLocalHazards(updatedHazards);

            // Push to pending changes
            if (mergePendingChanges) {
                const updatedSafetyEquipment: SafetyEquipment = {
                    ...safetyEquipment,
                    normalExposureLevel: localOverrides.normalExposureLevel,
                    highExposureLevel: localOverrides.highExposureLevel,
                    extremeExposureLevel: localOverrides.extremeExposureLevel,
                    safetyHazards: updatedHazards,
                };
                mergePendingChanges({
                    category: {
                        ...product.category,
                        safetyEquipment: updatedSafetyEquipment,
                    } as any,
                });
            }
        },
        [localHazards, localOverrides, mergePendingChanges, safetyEquipment, product.category]
    );

    // Handler for Static Accumulator change
    const handleStaticAccumulatorChange = useCallback(
        (value: number) => {
            setLocalStaticAccumulator(value);
            if (mergePendingChanges) {
                mergePendingChanges({
                    category: {
                        ...product.category,
                        isStaticAccumulator: value,
                    } as any,
                });
            }
        },
        [mergePendingChanges, product.category]
    );

    // Handler for Threshold Limit change
    const handleThresholdLimitChange = useCallback(
        (value: string) => {
            const numValue = value === '' ? null : parseFloat(value);
            setLocalThresholdLimit(numValue);
            if (mergePendingChanges) {
                mergePendingChanges({
                    category: {
                        ...product.category,
                        thresholdLimitValueTimeWeightedAverage: numValue,
                    } as any,
                });
            }
        },
        [mergePendingChanges, product.category]
    );

    // Handler for adding a Dräger Tube
    const handleAddDraegerTube = useCallback(
        (tubeId: string) => {
            if (!tubeId) return;

            const tubeToAdd = allDraegerTubes.find((t: any) => t.id.toString() === tubeId);
            if (!tubeToAdd) return;

            // Check if tube already exists
            if (localDraegerTubes.some((t: any) => t.id === tubeToAdd.id)) {
                setSelectedDraegerTubeId('');
                return;
            }

            const updatedTubes = [...localDraegerTubes, tubeToAdd];
            setLocalDraegerTubes(updatedTubes);
            setSelectedDraegerTubeId('');

            if (mergePendingChanges) {
                mergePendingChanges({
                    category: {
                        ...product.category,
                        draegerTubes: updatedTubes,
                    } as any,
                });
            }
        },
        [allDraegerTubes, localDraegerTubes, mergePendingChanges, product.category]
    );

    // Handler for removing a Dräger Tube
    const handleRemoveDraegerTube = useCallback(
        (tubeId: number) => {
            const updatedTubes = localDraegerTubes.filter((t: any) => t.id !== tubeId);
            setLocalDraegerTubes(updatedTubes);

            if (mergePendingChanges) {
                mergePendingChanges({
                    category: {
                        ...product.category,
                        draegerTubes: updatedTubes,
                    } as any,
                });
            }
        },
        [localDraegerTubes, mergePendingChanges, product.category]
    );

    // Handler for Comments (MSG) change
    const handleCommentsMSGChange = useCallback(
        (value: string) => {
            setLocalCommentsMSG(value);
            if (mergePendingChanges) {
                // Update or add comment with category 11
                const existingComment = product.comments?.find((c) => c.category === 11);
                const updatedComment = {
                    id: existingComment?.id || 0,
                    text: value,
                    category: 11,
                    visibility: existingComment?.visibility || 0,
                    lastUpdatedUtc: new Date().toISOString(),
                };
                mergePendingChanges({
                    comments: [updatedComment],
                });
            }
        },
        [mergePendingChanges, product.comments]
    );

    // Get hazards that can be added (not already in the product)
    const availableHazards = useMemo(() => {
        const currentHazardIds = new Set((showEditUI ? localHazards : safetyHazards).map((h) => h.id));
        return allHazards.filter((h: SafetyHazardItem) => !currentHazardIds.has(h.id) && (h.isActive ?? h.IsActive));
    }, [allHazards, localHazards, safetyHazards, showEditUI]);

    // Use local hazards in edit mode, otherwise use product hazards
    const displayHazards = showEditUI ? localHazards : safetyHazards;

    // Get comments - Category 8 (Safety/Emergency) and Category 11 (Safety/Emergency MSG)
    const safetyComments = product.category?.comments?.find((c) => c.category === 8);
    const safetyCommentsMSG = product.comments?.find((c) => c.category === 11);

    // Get Emergency Scenarios (Procedures with Category 4)
    const emergencyProcedures = product.procedures?.filter((p) => p.category === 4) || [];

    // Static Accumulator mapping (0=Unknown, 1=Yes, 2=No, 3=Possible)
    const getStaticAccumulatorText = (value: number): string => {
        switch (value) {
            case 1:
                return 'Yes';
            case 2:
                return 'No';
            case 3:
                return 'Possible';
            default:
                return 'Unknown';
        }
    };

    // Threshold Limit display
    const thresholdLimit = product.category?.thresholdLimitValueTimeWeightedAverage;
    const thresholdLimitText = thresholdLimit !== null && thresholdLimit !== undefined ? `${thresholdLimit} ppm` : 'N/A';

    // Dräger Tubes
    const draegerTubes = product.category?.draegerTubes || [];

    // Get unique hazard warning images - use displayHazards for edit mode
    const uniqueHazardImages = displayHazards
        .filter((h) => h.warningImage)
        .filter((h, index, self) => index === self.findIndex((t) => t.warningImage === h.warningImage));

    // DEBUG: Log MSDS data to console
    console.log('[SafetyInfo DEBUG] Product ID:', product.id);
    console.log('[SafetyInfo DEBUG] materialSafetyDataSheet:', product.materialSafetyDataSheet);
    console.log('[SafetyInfo DEBUG] materialSafetyDataSheet?.filePath:', product.materialSafetyDataSheet?.filePath);
    console.log('[SafetyInfo DEBUG] Full product object:', JSON.stringify(product, null, 2));

    return (
        <SectionCard>
            <SectionTitle>Safety Equipment</SectionTitle>

            <SafetyAccordionWrapper>
                <InfoBoxWrapper>
                    {/* Normal Exposure */}
                    <InfoBox>
                        <InfoBoxContentNormal>
                            <h2>Normal exposure</h2>
                            <LevelNumberBox>
                                <LevelNumber>
                                    {!showEditUI ? (
                                        <>
                                            <h3>{normalLevel ?? 'N/A'}</h3>
                                            <h4>Level of protection</h4>
                                        </>
                                    ) : (
                                        <EditModeOverwriteSection>
                                            <GeneratedValueLabel>
                                                Highest value from hazards
                                                <span>{exposureLevels.normalExposureLevelGenerated ?? 'N/A'}</span>
                                            </GeneratedValueLabel>
                                            <OverwriteLabel>Overwrite value</OverwriteLabel>
                                            <OverwriteInput
                                                type="number"
                                                min={0}
                                                max={4}
                                                value={localOverrides.normalExposureLevel ?? ''}
                                                onChange={(e) => handleExposureLevelChange('normal', e.target.value)}
                                                placeholder=""
                                            />
                                        </EditModeOverwriteSection>
                                    )}
                                </LevelNumber>
                            </LevelNumberBox>
                        </InfoBoxContentNormal>

                        <InfoTextContent>
                            <h5>What you need to wear</h5>
                            <ul>
                                {normalEquipment.length > 0 ? (
                                    normalEquipment.map((equipment: any, index: number) => (
                                        <li key={index}>{equipment.description || equipment.Description}</li>
                                    ))
                                ) : (
                                    <>
                                        <li>Chemical resistant gloves</li>
                                        <li>Safety glasses</li>
                                        <li>Coveralls</li>
                                    </>
                                )}
                            </ul>
                        </InfoTextContent>

                        <DescTextContent>
                            <h5 onClick={() => toggleDescription('normal')}>
                                What is Normal exposure
                                <a>
                                    <i className={openDescriptions.normal ? 'open' : ''}></i>
                                </a>
                            </h5>
                            {openDescriptions.normal && (
                                <ul>
                                    <li>Cargo watch, stripping</li>
                                    <li>Tank cleaning in closed condition</li>
                                    <li>Tank cleaning in open when gas free condition</li>
                                </ul>
                            )}
                        </DescTextContent>
                    </InfoBox>

                    {/* High Exposure */}
                    <InfoBox>
                        <InfoBoxContentHigh>
                            <h2>High exposure</h2>
                            <LevelNumberBox>
                                <LevelNumber>
                                    {!showEditUI ? (
                                        <>
                                            <h3>{highLevel ?? 'N/A'}</h3>
                                            <h4>Level of protection</h4>
                                        </>
                                    ) : (
                                        <EditModeOverwriteSection>
                                            <GeneratedValueLabel>
                                                Highest value from hazards
                                                <span>{exposureLevels.highExposureLevelGenerated ?? 'N/A'}</span>
                                            </GeneratedValueLabel>
                                            <OverwriteLabel>Overwrite value</OverwriteLabel>
                                            <OverwriteInput
                                                type="number"
                                                min={0}
                                                max={4}
                                                value={localOverrides.highExposureLevel ?? ''}
                                                onChange={(e) => handleExposureLevelChange('high', e.target.value)}
                                                placeholder=""
                                            />
                                        </EditModeOverwriteSection>
                                    )}
                                </LevelNumber>
                            </LevelNumberBox>
                        </InfoBoxContentHigh>

                        <InfoTextContent>
                            <h5>What you need to wear</h5>
                            <ul>
                                {highEquipment.length > 0 ? (
                                    highEquipment.map((equipment: any, index: number) => (
                                        <li key={index}>{equipment.description || equipment.Description}</li>
                                    ))
                                ) : (
                                    <>
                                        <li>Full face respirator</li>
                                        <li>Chemical resistant suit</li>
                                        <li>Chemical resistant boots</li>
                                    </>
                                )}
                            </ul>
                        </InfoTextContent>

                        <DescTextContent>
                            <h5 onClick={() => toggleDescription('high')}>
                                What is High exposure
                                <a>
                                    <i className={openDescriptions.high ? 'open' : ''}></i>
                                </a>
                            </h5>
                            {openDescriptions.high && (
                                <ul>
                                    <li>Connecting or disconnecting cargo hoses</li>
                                    <li>Sampling and Gauging</li>
                                    <li>Line blowing</li>
                                    <li>Work on PV mast</li>
                                    <li>Purging of cofferdam on the cargo Pump</li>
                                    <li>Circulating cleaning chemicals</li>
                                </ul>
                            )}
                        </DescTextContent>
                    </InfoBox>

                    {/* Extreme Exposure */}
                    <InfoBox>
                        <InfoBoxContentExtreme>
                            <h2>Extreme exposure</h2>
                            <LevelNumberBox>
                                <LevelNumber>
                                    {!showEditUI ? (
                                        <>
                                            <h3>{extremeLevel ?? 'N/A'}</h3>
                                            <h4>Level of protection</h4>
                                        </>
                                    ) : (
                                        <EditModeOverwriteSection>
                                            <GeneratedValueLabel>
                                                Highest value from hazards
                                                <span>{exposureLevels.extremeExposureLevelGenerated ?? 'N/A'}</span>
                                            </GeneratedValueLabel>
                                            <OverwriteLabel>Overwrite value</OverwriteLabel>
                                            <OverwriteInput
                                                type="number"
                                                min={0}
                                                max={4}
                                                value={localOverrides.extremeExposureLevel ?? ''}
                                                onChange={(e) => handleExposureLevelChange('extreme', e.target.value)}
                                                placeholder=""
                                            />
                                        </EditModeOverwriteSection>
                                    )}
                                </LevelNumber>
                            </LevelNumberBox>
                        </InfoBoxContentExtreme>

                        <InfoTextContent>
                            <h5>What you need to wear</h5>
                            <ul>
                                {extremeEquipment.length > 0 ? (
                                    extremeEquipment.map((equipment: any, index: number) => (
                                        <li key={index}>{equipment.description || equipment.Description}</li>
                                    ))
                                ) : (
                                    <>
                                        <li>Self-contained breathing apparatus</li>
                                        <li>Fully encapsulating suit</li>
                                        <li>Emergency equipment</li>
                                    </>
                                )}
                            </ul>
                        </InfoTextContent>

                        <DescTextContent>
                            <h5 onClick={() => toggleDescription('extreme')}>
                                What is Extreme exposure
                                <a>
                                    <i className={openDescriptions.extreme ? 'open' : ''}></i>
                                </a>
                            </h5>
                            {openDescriptions.extreme && (
                                <ul>
                                    <li>Contingency situation when there is a liquid spill or uncontrolled vapor release</li>
                                </ul>
                            )}
                        </DescTextContent>
                    </InfoBox>
                </InfoBoxWrapper>

                <DetailsColumn className={showEditUI ? 'edit-mode' : ''}>
                    {/* Add Hazard Dropdown - Only shown in edit mode */}
                    {showEditUI && (
                        <HazardSelect
                            value={selectedHazardId}
                            onChange={(e) => {
                                setSelectedHazardId(e.target.value);
                                handleAddHazard(e.target.value);
                            }}
                            aria-label="Add hazard to product"
                            title="Add hazard to product"
                        >
                            <option value="">Add hazard to product</option>
                            {availableHazards
                                .sort((a: SafetyHazardItem, b: SafetyHazardItem) =>
                                    (a.hazardCode || a.HazardCode || '').localeCompare(b.hazardCode || b.HazardCode || '')
                                )
                                .map((hazard: SafetyHazardItem) => (
                                    <option key={hazard.id} value={hazard.id.toString()}>
                                        {hazard.hazardCode || hazard.HazardCode} - {hazard.hazardDescription || hazard.HazardDescription}
                                    </option>
                                ))}
                        </HazardSelect>
                    )}

                    <HazardList>
                        {displayHazards.map((hazard) => (
                            <HazardItem key={hazard.id} className={showEditUI ? 'edit-mode' : ''}>
                                {showEditUI && (
                                    <HazardRemoveButton onClick={() => handleRemoveHazard(hazard.id)} title="Remove hazard from product">
                                        🗑️
                                    </HazardRemoveButton>
                                )}
                                <HazardCode>{hazard.hazardCode}</HazardCode>
                                <HazardDescription>{hazard.hazardDescription}</HazardDescription>
                            </HazardItem>
                        ))}
                    </HazardList>

                    <ImageWrapper>
                        {uniqueHazardImages.map((hazard) => (
                            <span key={hazard.id} title={hazard.hazardDescription}>
                                <img src={`/assets/images/${hazard.warningImage}`} alt={hazard.hazardDescription} />
                            </span>
                        ))}
                    </ImageWrapper>
                </DetailsColumn>
            </SafetyAccordionWrapper>

            {/* Changed Manually Message */}
            {manuallyChanged && (
                <ChangedManuallyMessage>* Level of protection has been changed manually by ChemGuard Administration</ChangedManuallyMessage>
            )}

            {/* N/A Message */}
            {naLevel && <NAMessage>** No exposure level available. Please contact MSG.</NAMessage>}

            {/* Safety/Emergency Comments (Category 8) */}
            {safetyComments && safetyComments.text && (
                <CommentsSection>
                    <CommentsLabel>Comments:</CommentsLabel>
                    <CommentsText>{safetyComments.text}</CommentsText>
                </CommentsSection>
            )}

            {/* Emergency Scenarios */}
            {emergencyProcedures.length > 0 && (
                <CommentsSection>
                    <SectionTitle>Emergency Scenarios</SectionTitle>
                    <EmergencyScenariosTable>
                        <tbody>
                            {emergencyProcedures.map((procedure) => (
                                <tr key={procedure.id}>
                                    <td className="procedure-title">
                                        {procedure.title} ({procedure.id})
                                    </td>
                                    <td>{procedure.description}</td>
                                </tr>
                            ))}
                        </tbody>
                    </EmergencyScenariosTable>
                </CommentsSection>
            )}

            {/* MSDS Link */}
            {product.materialSafetyDataSheet?.filePath && (
                <MsdsLinkWrapper>
                    <ExternalLink
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            downloadFile({ category: 'MSDS', fileName: product.materialSafetyDataSheet.filePath });
                        }}
                    >
                        Open the manufacturer&apos;s Material Safety Data Sheet.
                    </ExternalLink>
                </MsdsLinkWrapper>
            )}

            {/* Additional Safety Information - Stacked vertically with minimal spacing */}
            <StackedInfoSection>
                <InfoItem className="full-width-item">
                    <label>Static Accumulator:</label>
                    {showEditUI ? (
                        <EditSelect
                            value={localStaticAccumulator}
                            onChange={(e) => handleStaticAccumulatorChange(parseInt(e.target.value, 10))}
                            aria-label="Static Accumulator"
                            title="Static Accumulator"
                        >
                            <option value={0}>Unknown</option>
                            <option value={1}>Yes</option>
                            <option value={2}>No</option>
                            <option value={3}>Possible</option>
                        </EditSelect>
                    ) : (
                        <span>{getStaticAccumulatorText(product.category?.isStaticAccumulator || 0)}</span>
                    )}
                </InfoItem>

                <InfoItem className="full-width-item">
                    <label>Threshold Limit (TWA):</label>
                    {showEditUI ? (
                        <EditFieldWrapper>
                            <EditInput
                                type="number"
                                min={0}
                                step="0.1"
                                value={localThresholdLimit ?? ''}
                                onChange={(e) => handleThresholdLimitChange(e.target.value)}
                                placeholder=""
                            />
                            <InputSuffix>ppm</InputSuffix>
                        </EditFieldWrapper>
                    ) : (
                        <span>{thresholdLimitText}</span>
                    )}
                </InfoItem>

                {/* Dräger Tubes */}
                <InfoItem className="full-width-item">
                    <label>Dräger Tubes:</label>
                    {draegerTubes.length === 0 && !showEditUI ? (
                        <span>No Dräger tube selected</span>
                    ) : (
                        <>
                            <DraegerTubesList>
                                {(showEditUI ? localDraegerTubes : draegerTubes).map((tube: any, index: number) => (
                                    <React.Fragment key={tube.id}>
                                        <DraegerTubeItem className={showEditUI ? 'edit-mode' : ''}>
                                            {tube.isNoDraegerTubeAvailable ? (
                                                <span title="No Dräger tube available">{tube.orderCode}</span>
                                            ) : (
                                                <DraegerTubeLink
                                                    href="#"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        downloadFile({ category: 'DraegerTube', fileName: tube.filePath });
                                                    }}
                                                >
                                                    {tube.orderCode}
                                                </DraegerTubeLink>
                                            )}
                                            {showEditUI && (
                                                <DraegerTubeRemoveButton onClick={() => handleRemoveDraegerTube(tube.id)} title="Remove Dräger tube">
                                                    ×
                                                </DraegerTubeRemoveButton>
                                            )}
                                        </DraegerTubeItem>
                                        {!showEditUI && index < draegerTubes.length - 1 && <span>, </span>}
                                    </React.Fragment>
                                ))}
                            </DraegerTubesList>
                            {showEditUI && (
                                <AddDraegerTubeWrapper>
                                    <EditSelect
                                        value={selectedDraegerTubeId}
                                        onChange={(e) => {
                                            setSelectedDraegerTubeId(e.target.value);
                                            handleAddDraegerTube(e.target.value);
                                        }}
                                        aria-label="Add Dräger tube"
                                        title="Add Dräger tube"
                                    >
                                        <option value="">Add Dräger tube...</option>
                                        {allDraegerTubes
                                            .filter((t: any) => !localDraegerTubes.some((lt: any) => lt.id === t.id))
                                            .map((tube: any) => (
                                                <option key={tube.id} value={tube.id.toString()}>
                                                    {tube.orderCode} - {tube.substance || tube.Substance || 'Unknown'}
                                                </option>
                                            ))}
                                    </EditSelect>
                                </AddDraegerTubeWrapper>
                            )}
                        </>
                    )}
                </InfoItem>
            </StackedInfoSection>

            {/* Safety/Emergency Comments MSG (Category 11) */}
            {showEditUI && canEditChemGuard ? (
                <CommentsEditSection>
                    <CommentsEditLabel>Comments (MSG):</CommentsEditLabel>
                    <EditTextarea
                        value={localCommentsMSG}
                        onChange={(e) => handleCommentsMSGChange(e.target.value)}
                        placeholder="Enter comments for MSG..."
                    />
                </CommentsEditSection>
            ) : (
                safetyCommentsMSG &&
                safetyCommentsMSG.text && (
                    <CommentsSection>
                        <CommentsLabel>Comments (MSG):</CommentsLabel>
                        <CommentsText>{safetyCommentsMSG.text}</CommentsText>
                    </CommentsSection>
                )
            )}

            {/* EU Threshold Limits Link */}
            <CommentsSection>
                <ExternalLink href="https://limitvalue.ifa.dguv.de/WebForm_gw2.aspx" target="_blank" rel="noopener noreferrer">
                    EU Threshold limits
                </ExternalLink>
            </CommentsSection>
        </SectionCard>
    );
};

export default SafetyInformationSection;
